import {
  ModelProviderVO,
  SkillCategoryItem,
  SkillDetailVO,
  UseSkillItem,
  _chatGetConfig,
  _chatModelList,
  _chatUpdateConfig,
  _skillCategories,
  _skillDetailBatch,
  _skillList,
  _skillPickerFeatured,
  _skillPickerRecommend
} from '../services/aiAssistant';
import { isOidcRequiredError } from '../utils/aiAssistantOidc';
import { useEffect, useMemo, useState } from 'react';
import { SkillsDialogListParams, SkillsDialogLoaders } from './SkillsDialog';

interface Params {
  /** 工具条所在容器是否处于活跃态：抽屉传 open，常驻页面传 true。非活跃时清空所有配置（不缓存） */
  active: boolean;
  /** 命中 4001 需要 OIDC 授权时的回调，由调用方决定跳转时机与 pending 上下文 */
  onOidcRequired: () => void;
}

/**
 * 对话底部工具条的模型 / 技能配置态：
 * 模型清单与当前配置（含公用 + 个人技能）、技能详情、分类，以及模型切换与技能增删改的持久化。
 * 由浮窗抽屉与 AI 助手主页共用。
 */
export function useChatToolbarConfig({ active, onOidcRequired }: Params) {
  const [providers, setProviders] = useState<ModelProviderVO[]>([]);
  const [currentModelId, setCurrentModelId] = useState<string | null>(null);
  const [configLoaded, setConfigLoaded] = useState(false);
  const [selectedSkillIds, setSelectedSkillIds] = useState<number[]>([]);
  const [sharedSkillIds, setSharedSkillIds] = useState<number[]>([]);
  const [enabledSkillIds, setEnabledSkillIds] = useState<number[]>([]);
  const [skillDetailMap, setSkillDetailMap] = useState<Record<number, SkillDetailVO>>({});
  const [skillCategories, setSkillCategories] = useState<SkillCategoryItem[]>([]);
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [skillSubmitting, setSkillSubmitting] = useState(false);

  // 活跃 → 并发拉模型清单 + 当前配置 + Skill 分类；非活跃时重置（不缓存）
  useEffect(() => {
    if (!active) {
      setProviders([]);
      setCurrentModelId(null);
      setConfigLoaded(false);
      setSelectedSkillIds([]);
      setSharedSkillIds([]);
      setEnabledSkillIds([]);
      setSkillDetailMap({});
      setSkillCategories([]);
      setPopoverOpen(false);
      setDialogOpen(false);
      setSkillSubmitting(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const [list, cfg, catsRes] = await Promise.all([
          _chatModelList(),
          _chatGetConfig(),
          _skillCategories().catch((err) => {
            // 分类失败不影响其它块；OIDC 由下方统一兜底
            if (isOidcRequiredError(err)) throw err;
            return null;
          })
        ]);
        if (cancelled) return;
        setProviders(list || []);
        setCurrentModelId(cfg?.modelId ?? null);
        setConfigLoaded(true);
        setSkillCategories(catsRes?.categories || []);
        const shared = cfg?.sharedSkillIds ?? [];
        const personal = cfg?.personalSkillIds ?? cfg?.skillIds ?? [];
        setSharedSkillIds(shared);
        setSelectedSkillIds(personal);
        // enabledSkillIds 不缓存：每次活跃为空，发送后自动清空
        const mergedIds = Array.from(new Set<number>([...shared, ...personal]));
        if (mergedIds.length > 0) {
          try {
            const details = await _skillDetailBatch({ idList: mergedIds });
            if (cancelled) return;
            const map: Record<number, SkillDetailVO> = {};
            (details || []).forEach((d) => {
              if (d && typeof d.id === 'number') map[d.id] = d;
            });
            setSkillDetailMap(map);
          } catch (err) {
            if (isOidcRequiredError(err)) {
              onOidcRequired();
            }
            // 错误提示由 request 拦截器统一处理；detail 失败 → chip 显示占位
          }
        }
      } catch (err) {
        if (isOidcRequiredError(err)) {
          onOidcRequired();
        }
        // 错误提示由 request 拦截器统一处理；接口失败 → 隐藏 picker
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [active]);

  const handleModelChange = async (modelId: string) => {
    const prev = currentModelId;
    setCurrentModelId(modelId);
    try {
      await _chatUpdateConfig({ modelId, skillIds: selectedSkillIds });
    } catch {
      // 错误提示由 request 拦截器统一处理
      setCurrentModelId(prev);
    }
  };

  const refreshSkillDetail = async (ids: number[]) => {
    if (!ids.length) return;
    try {
      const details = await _skillDetailBatch({ idList: ids });
      const map: Record<number, SkillDetailVO> = {};
      (details || []).forEach((d) => {
        if (d && typeof d.id === 'number') map[d.id] = d;
      });
      setSkillDetailMap(map);
    } catch (err) {
      if (isOidcRequiredError(err)) {
        onOidcRequired();
      }
      // 错误提示由 request 拦截器统一处理
    }
  };

  const handleRemoveSkill = async (id: number) => {
    // 公用技能只读；非个人集内的 id 视为 no-op
    if (sharedSkillIds.includes(id) || !selectedSkillIds.includes(id)) return;
    const prevSelected = selectedSkillIds;
    const prevEnabled = enabledSkillIds;
    const nextSelected = prevSelected.filter((x) => x !== id);
    const nextEnabled = prevEnabled.filter((x) => x !== id);
    setSelectedSkillIds(nextSelected);
    setEnabledSkillIds(nextEnabled);
    try {
      await _chatUpdateConfig({
        modelId: currentModelId ?? undefined,
        skillIds: nextSelected
      });
    } catch (err) {
      // 错误提示由 request 拦截器统一处理
      setSelectedSkillIds(prevSelected);
      setEnabledSkillIds(prevEnabled);
      if (isOidcRequiredError(err)) {
        onOidcRequired();
      }
    }
  };

  const handleToggleEnabledSkill = (id: number) => {
    if (!selectedSkillIds.includes(id) && !sharedSkillIds.includes(id)) return;
    setEnabledSkillIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const handleConfirmSkills = async (ids: number[]) => {
    setSkillSubmitting(true);
    try {
      await _chatUpdateConfig({
        modelId: currentModelId ?? undefined,
        skillIds: ids
      });
      // 配置变更不影响"本次启用"：仅保留仍存在于配置中的启用项
      const idSet = new Set(ids);
      setSelectedSkillIds(ids);
      setEnabledSkillIds((prev) => prev.filter((id) => idSet.has(id) || sharedSkillIds.includes(id)));
      const mergedIds = Array.from(new Set<number>([...sharedSkillIds, ...ids]));
      if (mergedIds.length > 0) {
        await refreshSkillDetail(mergedIds);
      } else {
        setSkillDetailMap({});
      }
      setDialogOpen(false);
    } catch (err) {
      // 错误提示由 request 拦截器统一处理；保持 dialog 打开允许重试
      if (isOidcRequiredError(err)) {
        onOidcRequired();
      }
    } finally {
      setSkillSubmitting(false);
    }
  };

  const skillLoaders: SkillsDialogLoaders = useMemo(
    () => ({
      featured: async () => {
        try {
          return await _skillPickerFeatured();
        } catch (err) {
          if (isOidcRequiredError(err)) onOidcRequired();
          throw err;
        }
      },
      recommend: async () => {
        try {
          return await _skillPickerRecommend();
        } catch (err) {
          if (isOidcRequiredError(err)) onOidcRequired();
          throw err;
        }
      },
      list: async (params: SkillsDialogListParams) => {
        try {
          return await _skillList(params);
        } catch (err) {
          if (isOidcRequiredError(err)) onOidcRequired();
          throw err;
        }
      }
    }),
    []
  );

  /** 本次启用技能 → 请求级 useSkills 透传体；无启用项返回 undefined */
  const buildUseSkills = (): UseSkillItem[] | undefined =>
    enabledSkillIds.length
      ? enabledSkillIds.map((id) => {
          const detail = skillDetailMap[id];
          const name = detail?.title || detail?.name;
          return name ? { id, name } : { id };
        })
      : undefined;

  return {
    providers,
    currentModelId,
    configLoaded,
    selectedSkillIds,
    sharedSkillIds,
    enabledSkillIds,
    skillDetailMap,
    skillCategories,
    popoverOpen,
    setPopoverOpen,
    dialogOpen,
    setDialogOpen,
    skillSubmitting,
    handleModelChange,
    handleRemoveSkill,
    handleToggleEnabledSkill,
    handleConfirmSkills,
    skillLoaders,
    buildUseSkills,
    clearEnabledSkills: () => setEnabledSkillIds([])
  };
}
